import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  FulfillmentOrder,
  FulfillmentOrderApiService,
  FulfillmentOrderStatus,
  SearchFulfillmentOrderRequest,
} from '@vendasta/order-fulfillment';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import {
  ListSalesOrderRequestFilters,
  ListSalesOrderRequestSortDirection,
  ListSalesOrderRequestSortField,
  ListSalesOrderRequestSortOption,
  Order,
  SalesOrdersService,
  Status,
} from '@vendasta/sales-orders';
import moment from 'moment';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { Badge, StatusToBadgeMapping } from '../conversion-utils';

interface UIOrder {
  orderDetailsUrl$: Observable<string>;
  fulfillmentFormUrl$: Observable<string>;
  salesOrder: Order;
  badge: Badge;
  requestedActivation: string;
}
interface SalesOrderListResponse {
  hasMore: boolean;
  salesOrders: UIOrder[];
}

@Component({
  selector: 'sales-ui-sales-order-short-list',
  templateUrl: './sales-order-short-list.component.html',
  styleUrls: ['./sales-order-short-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SalesOrderShortListComponent implements OnInit {
  @Input() getOrderDetailsUrl: (orderId: string) => Observable<string>;
  @Input() getFulfillmentFormUrl: (orderId: string) => Observable<string>;
  @Input() ordersPageForBusinessUrl: string;
  @Input() ordersPageForBusinessQuery: Record<string, string>;
  @Input() partnerId: string;
  @Input() businessId: string;
  @Input() showCreateButton = false;
  @Output() createClicked: EventEmitter<boolean> = new EventEmitter();

  loading = false;
  error = false;
  response$: Observable<SalesOrderListResponse>;
  actionRequiredForFO$: Observable<Map<string, boolean>>;

  constructor(
    private salesOrdersService: SalesOrdersService,
    private cdk: ChangeDetectorRef,
    private fulfillmentOrderApiService: FulfillmentOrderApiService,
    private analyticsService: ProductAnalyticsService,
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.response$ = this.salesOrdersService
      .list(
        this.partnerId,
        new ListSalesOrderRequestFilters({ businessId: this.businessId }),
        new ListSalesOrderRequestSortOption({
          field: ListSalesOrderRequestSortField.CREATED,
          direction: ListSalesOrderRequestSortDirection.DESCENDING,
        }),
        '',
        5,
      )
      .pipe(
        take(1),
        map((resp) => {
          this.loading = false;
          return {
            hasMore: resp.hasMore,
            salesOrders: (resp.results || []).map((r) => {
              return {
                orderDetailsUrl$: this.getOrderDetailsUrl(r.orderId),
                fulfillmentFormUrl$: this.getFulfillmentFormUrl(r.orderId),
                salesOrder: r,
                badge: StatusToBadgeMapping.get(r.status) || StatusToBadgeMapping.get(Status.SUBMITTED),
                requestedActivation: moment(r.requestedActivation).format('ll'),
              } as UIOrder;
            }),
          };
        }),
        catchError((e) => {
          this.loading = false;
          this.error = true;
          this.cdk.detectChanges();
          return throwError(e);
        }),
      );
    this.actionRequiredForFO$ = this.response$.pipe(
      filter((response) => response?.salesOrders?.length > 0),
      take(1),
      switchMap((response) => {
        return this.fulfillmentOrderApiService.searchFulfillmentOrder(
          new SearchFulfillmentOrderRequest({
            pageSize: 20,
            cursor: '',
            salesOrderIds: response.salesOrders.map((x) => x.salesOrder.orderId),
            businessIds: [this.businessId],
          }),
        );
      }),
      map((response) => {
        if (response?.fulfillmentOrders?.length > 0) {
          this.analyticsService.trackEvent(
            'account-details-work-order-banner-present',
            'tm',
            'account-details-work-order-banner-present',
          );
          return SalesOrderShortListComponent.createActionRequiredMapFromFulfillmentOrders(response.fulfillmentOrders);
        }
        return new Map();
      }),
    );
  }

  static createActionRequiredMapFromFulfillmentOrders(fulfillmentOrders: FulfillmentOrder[]): Map<string, boolean> {
    const result = new Map<string, boolean>();
    fulfillmentOrders?.forEach((fo) => {
      // if it already found a Fulfillment Order in Details Needed Status for a specific sales order, do not overwrite
      if (!result.get(fo.salesOrderId)) {
        result.set(fo.salesOrderId, fo.status === FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_DETAILS_NEEDED);
      }
    });
    return result;
  }

  create(): void {
    this.createClicked.emit(true);
  }
}
